import { ReactComponent as StatusIcon } from '@assets/components/panel/monitoring/status.svg';
import { IcChevronRight, IcDataInfoDocument, IcKey } from '@assets/icons/24x24';
import { IcNeutralTarget } from '@assets/icons/32x32';
import { Button, Center, createListCollection, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import StatusBar from '@components/common/StatusBar';
import { useMapContext } from '@components/map/MapContext';
import BlastEngagementActions from '@components/map/popup/engagement/BlastEngagementActions';
import { CustomSelect, Option } from '@components/ui/select/Select';
import { ToastStatusEnum } from '@components/ui/toaster';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { useWithDispatch } from '@hooks/useWithDispatch';
import {
  hasSensorRoleFromActiveProfile,
  selectActiveSite,
  selectActiveUserLogin,
  selectActiveUserSafetyKey,
} from '@redux/authent/authent.selectors';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectPopupsByMapId } from '@redux/maps/maps.selectors';
import {
  selectJammingNeutralSensorStatus,
  selectNeutralActiveMonitoringeSensorWithSectors,
} from '@redux/situation/monitoring.selectors';
import { selectTargets } from '@redux/situation/situation.selectors';
import { usePostMajesCommandMutation } from '@services/c2/c2.api';
import { booleanPointInPolygon, pointToPolygonDistance } from '@turf/turf';
import { formatDurationFromMillis } from '@utils/date.utils';
import { getSector } from '@utils/map/sector.utils';
import { checkIfPopupIsFromFeatureValue, findPopupByType } from '@utils/popup.utils';
import {
  getJammingCommand,
  getSafetyKeyColor,
  getSectorConfigurations,
  getSectorId,
  getSectorName,
  getSelectedId,
  getSelectedSectorConfiguration,
  isBlastConfigSet,
  isJamming,
  isWrongFox,
  noOperationalModules,
  sortSectorOption,
} from '@utils/sensors/neutral/neutral.utils';
import { createSensorToast, getSensorUniqueKey } from '@utils/sensors/sensors.utils';
import { toLonLatArray } from '@utils/validation/coordinates.utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { MajesSetSectorModeCommand, Mc2CommandEnum, NeutralInfo, Target } from '@/types/c2/c2.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { InfoDataTypeEnum, PopupType, PopupTypeEnum } from '@/types/popup.types';
import { ActiveMonitoringSensorByType } from '@/types/sensor/monitoring.types';
import { SensorFamilyEnum, TargetLocation } from '@/types/sensor/sensor.types';
import { JammingCommandTypeMap, Mc2SectorConfigMap, SectorConfigKey, SensorTypeEnum } from '@/types/sensor/sensors';

type Props = {
  neutralInfo: NeutralInfo;
};

export interface SectorOption<T extends SectorConfigKey = SectorConfigKey> extends Option {
  sensorType: T;
  sectorConfig: Mc2SectorConfigMap[T];
  monitoring: ActiveMonitoringSensorByType<T>;
  contains: boolean;
  dist: number;
  affectedTargetCount: number;
}

export type BlastSectorOption = SectorOption<SensorTypeEnum.BLAST>;

type SectorOptionMap = {
  [SensorTypeEnum.DFB6]: SectorOption<SensorTypeEnum.DFB6>;
  [SensorTypeEnum.MAJES]: SectorOption<SensorTypeEnum.MAJES>;
  [SensorTypeEnum.BLAST]: BlastSectorOption;
};

type SectorOptionUnion = SectorOptionMap[keyof SectorOptionMap];

function isBlastOption(option: SectorOptionUnion): option is BlastSectorOption {
  return option.sensorType === SensorTypeEnum.BLAST;
}

function Label(option: Readonly<SectorOption>) {
  return (
    <HStack gap={3} width="100%" height="68px" padding={0.5}>
      <StatusBar
        size="lg"
        activated={option.sectorConfig.active}
        sensorOrSubSensorInfo={{
          ...option.monitoring,
          maintenance: option.monitoring.configuration.maintenance,
        }}
      />
      <VStack gap={1} width="100%" alignItems="start">
        <HStack gap={1} width="100%" height="32px">
          <Text textStyle="lg" fontWeight="medium">
            {option.monitoring.configuration.name}
          </Text>
          {option.contains && <Icon width="32px" height="32px" as={IcNeutralTarget} />}
        </HStack>
        <HStack gap={1.5}>
          <Text textStyle="md">{option.label}</Text>
          <Text textStyle="md" color="neutral.300">
            <FormattedMessage
              id="components.popups.engagement.targets"
              values={{ count: option.affectedTargetCount }}
            />
          </Text>
        </HStack>
      </VStack>
    </HStack>
  );
}

export default function EngagementSelector({ neutralInfo }: Readonly<Props>) {
  const { mapId } = useMapContext();
  const { formatMessage } = useIntl();

  const updatePopup = useWithDispatch(updatePopupByMapId);
  const popups = useSelectorWithMapId(selectPopupsByMapId);

  const targets: Target<TargetLocation>[] = useSelectorWithReplayMode(selectTargets);

  const neutralActiveMonitoringSensors = useSelectorWithReplayMode(selectNeutralActiveMonitoringeSensorWithSectors);
  const jammingSensorStatus = useSelectorWithReplayMode(selectJammingNeutralSensorStatus);
  const isSensorJamming = jammingSensorStatus !== null;
  const backgroundGif = isSensorJamming ? `url(/jamming-animated-background.gif)` : undefined;
  const neutralSensorAction = { action: ActionEnum.COMMAND, sensorFamily: SensorFamilyEnum.NEUTRAL };

  const accessNeutralCommand = useAppSelector((state) => hasSensorRoleFromActiveProfile(state, neutralSensorAction));
  const activeSite = useAppSelector(selectActiveSite);
  const activeUserLogin = useAppSelector(selectActiveUserLogin);
  const isNonActiveUserJamming = isSensorJamming && neutralInfo.login !== null && activeUserLogin !== neutralInfo.login;

  const [postMajesCommand, { isLoading }] = usePostMajesCommandMutation();

  const safetyKey = useAppSelector(selectActiveUserSafetyKey);
  const hasSafetyKeyNotLocked = safetyKey?.active && !safetyKey.locked;

  const sectorOptions = useMemo(() => {
    return neutralActiveMonitoringSensors
      .flatMap((monitoring) =>
        getSectorConfigurations(monitoring)
          .filter((sector) => sector.active)
          .map((sectorConfig) => ({
            sectorConfig,
            monitoring,
          })),
      )
      .map(({ sectorConfig, monitoring }) => {
        const bearing1 = sectorConfig.orientation - sectorConfig.aperture / 2;
        const bearing2 = sectorConfig.orientation + sectorConfig.aperture / 2;
        const sectorPolygon = getSector(
          monitoring.configuration.sensorPosition,
          bearing1,
          bearing2,
          sectorConfig.range,
        )!;
        const objectivePosition = toLonLatArray(neutralInfo.coordinates, false)!;
        const contains = booleanPointInPolygon(objectivePosition, sectorPolygon);
        const dist = pointToPolygonDistance(objectivePosition, sectorPolygon);
        const affectedTargetCount = targets.reduce((acc, curr) => {
          if (booleanPointInPolygon(toLonLatArray(curr.lastPosition.location?.position)!, sectorPolygon)) {
            return acc + 1;
          }
          return acc;
        }, 0);
        return {
          sectorConfig,
          sensorType: monitoring.type,
          monitoring,
          contains,
          dist,
          disabled:
            jammingSensorStatus !== null &&
            getSensorUniqueKey(jammingSensorStatus) !== getSensorUniqueKey(monitoring.status),
          affectedTargetCount,
          label: getSectorName(sectorConfig),
          value: `${monitoring.configuration.uniqueKey}_${getSectorId(sectorConfig)}`,
        } as SectorOptionUnion;
      });
  }, [jammingSensorStatus, neutralInfo.coordinates, neutralActiveMonitoringSensors, targets]);
  const [order, setOrder] = useState<string[]>(sectorOptions.toSorted(sortSectorOption).map((option) => option.value));
  const defaultSelectedSectorOption = sectorOptions.toSorted(sortSectorOption)[0] as SectorOptionUnion | undefined;
  const [selectedSectorUniqueId, setSelectedSectorUniqueId] = useState<
    { uniqueSensorKey: string; id: number } | undefined
  >(
    defaultSelectedSectorOption
      ? {
          uniqueSensorKey: defaultSelectedSectorOption.monitoring.status.uniqueKey,
          id: getSectorId(defaultSelectedSectorOption.sectorConfig),
        }
      : undefined,
  );
  const selectedSector = sectorOptions.find(
    (sectorOption) =>
      sectorOption.monitoring.status.uniqueKey === selectedSectorUniqueId?.uniqueSensorKey &&
      getSectorId(sectorOption.sectorConfig) === selectedSectorUniqueId.id,
  );

  const [blastForceActiveMode, setBlastForceActiveMode] = useState(false);

  const wrongFox = isWrongFox(selectedSector, sectorOptions);

  const configSet = selectedSector ? (isBlastOption(selectedSector) ? isBlastConfigSet(selectedSector) : true) : false;

  const noModules = selectedSector && noOperationalModules(selectedSector.monitoring.configuration);

  const setSelectedSector = (sectorOption: SectorOption | undefined) => {
    if (!sectorOption) {
      setSelectedSectorUniqueId(undefined);
    } else {
      setSelectedSectorUniqueId({
        uniqueSensorKey: sectorOption.monitoring.status.uniqueKey,
        id: getSectorId(sectorOption.sectorConfig),
      });
    }
  };

  const [selectedSectorAfterStopJamming, setSelectedSectorAfterStopJamming] = useState<SectorOption | undefined>();

  const jammingDuration =
    neutralInfo.engagementSequenceDto?.jammingDuration && neutralInfo.engagementSequenceDto.jammingDuration !== 0
      ? formatDurationFromMillis(neutralInfo.engagementSequenceDto.jammingDuration * 1000, [
          'hours',
          'minutes',
          'seconds',
        ])
      : '';

  useEffect(() => {
    if (!isSensorJamming && selectedSectorAfterStopJamming) {
      setSelectedSector(selectedSectorAfterStopJamming);
    }
  }, [isSensorJamming, selectedSectorAfterStopJamming]);

  const handleChangeSector = (sectorOption: SectorOption | undefined) => {
    if (isNonActiveUserJamming || !sectorOption) {
      return;
    }
    if (isJamming(sectorOption.monitoring.status)) {
      setSelectedSectorAfterStopJamming(sectorOption);
    } else {
      setSelectedSector(sectorOption);
    }
    const toastParams = {
      command: 'sensors.commands.neutral.changeSectorCommand',
      name: sectorOption.monitoring.configuration.name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    postMajesCommand({
      appCode: sectorOption.monitoring.configuration.appCode,
      site: activeSite?.code ?? '',
      command: {
        type: Mc2CommandEnum.SetSectorMode,
        sensorCode: sectorOption.monitoring.configuration.code,
        sectorMode: getSectorId(sectorOption.sectorConfig),
      } as MajesSetSectorModeCommand,
    })
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  };

  const handleJamming = () => {
    if (isNonActiveUserJamming || !hasSafetyKeyNotLocked) {
      return;
    }
    if (selectedSector && accessNeutralCommand) {
      if (!isSensorJamming && isWrongFox(selectedSector, sectorOptions)) {
        console.error('Forbidden fox');
        return;
      }
      const toastParams = {
        command: 'sensors.commands.neutral.jammingCommand',
        name: selectedSector.monitoring.configuration.name,
      };
      createSensorToast({
        ...toastParams,
        toast: ToastStatusEnum.INFO,
      });

      const jammingCommandType = isSensorJamming
        ? Mc2CommandEnum.StopJamming
        : JammingCommandTypeMap[selectedSector.sensorType];

      const jammingCommand = getJammingCommand(selectedSector, jammingCommandType, blastForceActiveMode);

      postMajesCommand({
        appCode: selectedSector.monitoring.configuration.appCode,
        site: activeSite?.code ?? '',
        command: jammingCommand,
      })
        .unwrap()
        .then(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.SUCCESS,
          }),
        )
        .catch(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.ERROR,
          }),
        );
    }
  };

  useEffect(() => {
    if (jammingSensorStatus && !isLoading) {
      const jammingSectorConfiguration = getSelectedSectorConfiguration(jammingSensorStatus);
      jammingSectorConfiguration &&
        setSelectedSector(
          sectorOptions.find(
            (sectorOption) =>
              isJamming(sectorOption.monitoring.status) &&
              getSectorId(jammingSectorConfiguration) === getSectorId(sectorOption.sectorConfig),
          ),
        );
    }
  }, [isLoading, isSensorJamming, jammingSensorStatus, sectorOptions]);

  const canOpenPopup =
    selectedSector &&
    !checkIfPopupIsFromFeatureValue(findPopupByType(popups, PopupTypeEnum.INFO, InfoDataTypeEnum.INFO_SENSOR), {
      sensorUniqueKey: getSensorUniqueKey(selectedSector.monitoring.configuration),
    });

  const handleOpenPopup = () => {
    if (selectedSector && canOpenPopup) {
      updatePopup({
        mapId,
        popup: {
          type: PopupTypeEnum.INFO,
          position: { x: 500, y: 150 },
          data: {
            type: InfoDataTypeEnum.INFO_SENSOR,
            featureValue: { sensorUniqueKey: selectedSector.monitoring.configuration.uniqueKey },
          },
        } as PopupType,
      });
    }
  };

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (ref.current?.firstChild && (ref.current.firstChild as Element).getAttribute('data-state') !== 'open') {
      setOrder(sectorOptions.toSorted(sortSectorOption).map((option) => option.value));
    }
  }, [sectorOptions]);

  const items = createListCollection({
    items: sectorOptions.toSorted((a, b) => {
      const indexA = order.indexOf(a.value);
      const indexB = order.indexOf(b.value);

      const safeIndexA = indexA === -1 ? Infinity : indexA;
      const safeIndexB = indexB === -1 ? Infinity : indexB;

      return safeIndexA - safeIndexB;
    }),
  });

  useEffect(() => {
    if (
      !isSensorJamming &&
      selectedSector &&
      getSelectedId(selectedSector.monitoring) !== getSectorId(selectedSector.sectorConfig)
    ) {
      handleChangeSector(selectedSector);
    }
    //When we open the popup, we send a select command for the default selected value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <VStack padding={4} gap={3} backgroundColor="neutral.700" width="100%">
        <VStack width="100%" gap={1.5} alignItems="start">
          <Text textStyle="md" color="neutral.300">
            <FormattedMessage id="components.popups.engagement.jammer" />
          </Text>
          <CustomSelect<SectorOptionUnion>
            size="lg"
            ref={ref}
            value={selectedSector?.value}
            collection={items}
            disabled={isNonActiveUserJamming}
            onChange={(value) => handleChangeSector(sectorOptions.find((sectorOption) => sectorOption.value === value))}
            formatLabel={(option) => <Label {...option} />}
          />
          {selectedSector && isBlastOption(selectedSector) && (
            <BlastEngagementActions
              blastConfigAndStatus={selectedSector}
              forceActiveMode={blastForceActiveMode}
              onForceActiveModeChange={setBlastForceActiveMode}
              selectedSector={selectedSector}
              isJamming={isSensorJamming}
              targetId={neutralInfo.targetId}
            />
          )}
          <HStack
            className="group"
            border="2px solid"
            left={4}
            cursor={canOpenPopup ? 'pointer' : 'default'}
            borderColor="neutral.800"
            width="100%"
            padding={2}
            gap={2}
            marginTop={1.5}
            onClick={handleOpenPopup}
          >
            <Icon as={IcDataInfoDocument} boxSize="24px" color="neutral.300" />
            <Text>
              <FormattedMessage id="components.popups.engagement.info" />
            </Text>
            <Icon
              as={IcChevronRight}
              boxSize="24px"
              color="neutral.300"
              marginLeft="auto"
              _groupHover={{ color: canOpenPopup ? 'skyBlue' : 'neutral.300' }}
            />
          </HStack>
        </VStack>
      </VStack>
      <Center
        width="100%"
        height="200px"
        position="relative"
        backgroundColor="red.900"
        backgroundRepeat="no-repeat"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundImage={backgroundGif}
        userSelect="none"
      >
        {!isSensorJamming ? (
          <VStack position="absolute" left={4} gap={1} padding={1} border="2px solid" borderColor="red.800">
            <Icon as={IcKey} boxSize="24px" color="neutral.300" />
            <Icon as={StatusIcon} boxSize="16px" color={getSafetyKeyColor(safetyKey)} />
          </VStack>
        ) : null}
        {isNonActiveUserJamming ? (
          <Tooltip
            content={formatMessage(
              {
                id: 'components.popups.engagement.jammingBy',
              },
              { name: neutralInfo.login },
            )}
          >
            <Text paddingX={3} paddingY={2} textAlign="center" textStyle="md" backgroundColor="neutral.900-op75">
              <FormattedMessage id="components.popups.engagement.jammingOngoing" />
            </Text>
          </Tooltip>
        ) : (
          <Tooltip
            content={formatMessage({
              id: `sensors.tab.commands.dfb6.${isWrongFox(selectedSector, sectorOptions) ? 'wrongFox' : 'noOperationalModules'}`,
            })}
            positioning={{
              placement: 'top',
            }}
            openDelay={300}
            disabled={!(wrongFox || noModules)}
          >
            <VStack gap={3}>
              {jammingSensorStatus && (
                <Text textAlign="center" textStyle="md" whiteSpace="pre-line">
                  <FormattedMessage id="components.popups.engagement.jammingDuration" values={{ jammingDuration }} />
                </Text>
              )}
              <Button
                variant="jamming"
                onClick={handleJamming}
                loading={isLoading}
                disabled={
                  !accessNeutralCommand ||
                  sectorOptions.length === 0 ||
                  !selectedSector ||
                  noOperationalModules(selectedSector.monitoring.configuration) ||
                  !hasSafetyKeyNotLocked ||
                  wrongFox ||
                  !configSet
                }
              >
                <Text textStyle="md">
                  <FormattedMessage id="components.popups.engagement.jamming" values={{ isSensorJamming }} />
                </Text>
              </Button>
            </VStack>
          </Tooltip>
        )}
      </Center>
    </>
  );
}
